import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getParameterFromManager } from "./Utils";
import { getMergedMeaValidationCheckRules } from './Helper';

dayjs.extend(utc);
dayjs.extend(timezone);

const DEFAULT_TIME_ZONE = "Etc/GMT";
const DEFAULT_DATE_FORMAT = "DD-MM-YYYY";
const DEFAULT_TIME_ZONE_LABEL = "UTC";

let defaultTimeZone = DEFAULT_TIME_ZONE;
let defaultTimeZoneLabel = DEFAULT_TIME_ZONE_LABEL;
let defaultDateFormat = DEFAULT_DATE_FORMAT;

let timeZoneConfigPromise = null;
let dateFormatConfigPromise = null;

/**
 * Parse a JSON string or return an already-parsed value.
 */
function parseParameterValue(value) {
    if (typeof value !== "string") {
        return value;
    }
    try {
        return JSON.parse(value);
    } catch {
        return null;
    }
}

/**
 * Initialize timezone from MEA_TIMEZONE.
 *
 * Priority:
 * 1. displaytimezone, when configured
 * 2. serverTimeZone and its matching tz_difference entry
 * 3. Etc/GMT fallback
 */
export async function initializeDateTimeZone() {
    if (!timeZoneConfigPromise) {
        timeZoneConfigPromise = getParameterFromManager("MEA_TIMEZONE")
            .then((value) => {
                const config = parseParameterValue(value);

                const displayEntry = Object.entries(
                    config?.displaytimezone ?? {}
                ).find(
                    ([label, zone]) =>
                        typeof label === "string" &&
                        label.trim() &&
                        isValidTimeZone(zone)
                );

                const serverTimeZone = config?.serverTimeZone;

                const selectedLabel = displayEntry
                    ? displayEntry[0]
                    : serverTimeZone;

                const selectedTimeZone = displayEntry
                    ? displayEntry[1]
                    : config?.tz_difference?.[selectedLabel];

                const isValid = isValidTimeZone(selectedTimeZone);

                defaultTimeZone = isValid
                    ? selectedTimeZone
                    : DEFAULT_TIME_ZONE;

                defaultTimeZoneLabel = isValid
                    ? (selectedLabel === "UTC/GMT"
                        ? "UTC"
                        : selectedLabel || DEFAULT_TIME_ZONE_LABEL)
                    : DEFAULT_TIME_ZONE_LABEL;
            })
            .catch((error) => {
                console.warn(
                    "Failed to load timezone configuration:",
                    error
                );

                defaultTimeZone = DEFAULT_TIME_ZONE;
                defaultTimeZoneLabel = DEFAULT_TIME_ZONE_LABEL;
                timeZoneConfigPromise = null;
            });
    }

    await timeZoneConfigPromise;
}

/**
 * 
 * @param {*} timeZone 
 * @returns 
 */
function isValidTimeZone(timeZone) {
    if (typeof timeZone !== "string" || !timeZone.trim()) {
        return false;
    }
    try {
        new Intl.DateTimeFormat("en-US", {
            timeZone
        });
        return true;
    } catch {
        return false;
    }
}

/**
 * Initialize date format from MEA_VALIDATION_CHECK.
 *
 * Supports:
 * default.default_date_format
 * default.date_format
 *
 * Falls back to DD-MM-YYYY.
 */

export async function initializeDateFormat(domain) {
    if (!dateFormatConfigPromise) {
        dateFormatConfigPromise = getParameterFromManager(
            "MEA_VALIDATIONS_CHECK"
        )
            .then((meaValidationsCheck) => {
                const configKey =
                    domain &&
                        meaValidationsCheck?.[domain] !== undefined
                        ? domain
                        : "default";

                const config = getMergedMeaValidationCheckRules(
                    meaValidationsCheck,
                    configKey
                );

                const configuredFormat =
                    config?.default_date_format ??
                    config?.date_format;

                defaultDateFormat =
                    typeof configuredFormat === "string" &&
                        configuredFormat.trim()
                        ? configuredFormat.trim()
                        : DEFAULT_DATE_FORMAT;

            })
            .catch((error) => {
                console.error(
                    "Failed to load date format configuration:",
                    error
                );

                defaultDateFormat = DEFAULT_DATE_FORMAT;
                dateFormatConfigPromise = null;
            });
    }

    await dateFormatConfigPromise;
}


/**
 * Format a date using the configured date format and timezone.
 *
 * @param {string|Date} dateTime - Input date or timestamp.
 * @param {boolean} includeTime - Include time and timezone label.
 * @param {boolean} isUTC - Interpret the input as UTC and convert it.
 * @returns {string}
 */
export function formatDefaultDate(
    dateTime,
    includeTime = false,
    isUTC = false,
) {
    if (
        dateTime === null ||
        dateTime === undefined ||
        dateTime === "" ||
        dateTime === "-"
    ) {
        return "";
    }

    const effectiveFormat = includeTime
        ? `${defaultDateFormat} HH:mm:ss`
        : defaultDateFormat;

    let parsedDate;

    try {
        parsedDate = isUTC
            ? dayjs.utc(dateTime).tz(defaultTimeZone)
            : dayjs.tz(dateTime, defaultTimeZone);
    } catch {
        return "";
    }

    if (!parsedDate.isValid()) {
        return "";
    }

    const formattedDate = parsedDate.format(effectiveFormat);

    if (!includeTime) {
        return formattedDate;
    }

    return `${formattedDate} ${defaultTimeZoneLabel}`;
}